import { calculateChecksum, arrayBufferToBase64 } from './utils.js';
import { EventEmitter } from 'events';
const SERVICES = {
  ISSC_PROPRIETARY_SERVICE: '49535343-fe7d-4ae5-8fa9-9fafd205e455',
  DEVICE_INFO_SERVICE: '0000180a-0000-1000-8000-00805f9b34fb',
  GAP_SERVICE: '00001800-0000-1000-8000-00805f9b34fb',
  MAIN_SERVICE: '0000fff0-0000-1000-8000-00805f9b34fb',
};
const CHARACTERISTICS = {
  ISSC_TRANS_TX: '0000fff1-0000-1000-8000-00805f9b34fb',
  ISSC_TRANS_RX: '0000fff2-0000-1000-8000-00805f9b34fb',
};

const COMMANDS = {
  CMD_ENROLHOST: 0x07,
  CMD_CAPTUREHOST: 0x08,
  CMD_GETBAT: 0x21,
};
const HEADER_SIZE = 9;
const COMMAND_TIMEOUT_MS = 30 * 1000;
const FULL_BATTERY_VOLTAGE = 4.2;
export class BluetoothFingerprintScanner extends EventEmitter {
  constructor() {
    super();
    this.device = null;
    this.server = null;
    this.transTxCharacteristic = null;
    this.transRxCharacteristic = null;
    this.commandTimeoutId = null;
    this.resetData();
  }

  resetData() {
    this.mCmdData = new Uint8Array(4096); // Buffer for incoming data
    this.mCmdSize = 0; // Current size of the data in the buffer
    this.commandTimeoutId = null;
  }
  async connect() {
    this.device = await navigator.bluetooth.requestDevice({
      filters: [
        {
          services: [
            // SERVICES.ISSC_PROPRIETARY_SERVICE,
            // SERVICES.DEVICE_INFO_SERVICE,
            // SERVICES.GAP_SERVICE,
            SERVICES.MAIN_SERVICE,
          ],
        },
      ],
      optionalServices: [
        SERVICES.ISSC_PROPRIETARY_SERVICE,
        SERVICES.DEVICE_INFO_SERVICE,
        SERVICES.GAP_SERVICE,
        SERVICES.MAIN_SERVICE,
      ],
    });
    return this.device;
  }
  async connectService() {
    this.server = await this.device.gatt.connect();
    const service = await this.server.getPrimaryService(SERVICES.MAIN_SERVICE);

    this.device.addEventListener('gattserverdisconnected', (event) => {
      this.emit('disconnect');
    });

    this.transTxCharacteristic = await service.getCharacteristic(
      CHARACTERISTICS.ISSC_TRANS_TX,
    );
    this.transRxCharacteristic = await service.getCharacteristic(
      CHARACTERISTICS.ISSC_TRANS_RX,
    );

    this.transTxCharacteristic.addEventListener(
      'characteristicvaluechanged',
      this.handleNotifications.bind(this),
    );
    await this.transTxCharacteristic.startNotifications();
    this.emit('connected');
  }
  // Handle incoming notifications
  handleNotifications(event) {
    const value = event.target.value;
    // Convert received buffer to a byte array
    let incomingData = new Uint8Array(value.buffer);

    // Append new data to existing buffer
    this.mCmdData.set(incomingData, this.mCmdSize);
    this.mCmdSize += incomingData.length;

    // Check if we have enough data for header and checksum
    if (this.mCmdSize < 9) {
      return;
    }

    // Extract size from buffer
    const size = this.mCmdData[5] + (this.mCmdData[6] << 8);
    const totalSize = size + HEADER_SIZE; // Total message size including header and checksum

    // Check if the full message has been received
    if (this.mCmdSize < totalSize) {
      return;
    }

    // Extract command information
    const payloadSize = size - 1;
    const command = this.mCmdData[4];
    const payload = this.mCmdData.subarray(8, 8 + payloadSize);
    const checksumReceived =
      this.mCmdData[7 + size] + (this.mCmdData[8 + size] << 8);

    const checksumCalculated = calculateChecksum(
      this.mCmdData.subarray(0, 7 + size),
    );
    if (checksumReceived !== checksumCalculated) {
      console.error('Checksum mismatch');
      this.resetData(); // Reset data for next message
      this.emit('commandError', 'Checksum mismatch');
      return 'Checksum error';
    }
    if (this.mCmdData[0] !== 70 && this.mCmdData[1] !== 84) {
      this.emit('commandError', 'Invalid header');
      return;
    }

    clearTimeout(this.commandTimeoutId);
    this.resetData();
    this.handleCommand(command, payload);
    // this.onDataReceived(command, payload);
  }

  handleCommand(command, payload) {
    switch (command) {
      case COMMANDS.CMD_ENROLHOST:
        return this.handleEnrollmentData(payload);
      case COMMANDS.CMD_CAPTUREHOST:
        return this.handleCaptureData(payload);
      case COMMANDS.CMD_GETBAT:
        const batteryVoltage = payload[0] / 10;

        this.emit(
          'battery',
          Math.floor(
            batteryVoltage
              ? (batteryVoltage / FULL_BATTERY_VOLTAGE) * 100
              : 100,
          ),
        );
      default:
        return 'Unknown command response';
    }
  }

  // Handle specific data parsing for enrollment
  handleEnrollmentData(data) {
    const base64String = arrayBufferToBase64(data);
    this.emit('enroll', base64String);
  }

  // Handle specific data parsing for capture
  handleCaptureData(data) {
    const base64String = arrayBufferToBase64(data);
    this.emit('capture', base64String);
  }

  // Handle specific data parsing for image
  getBattery() {
    return this.sendCommand(COMMANDS.CMD_GETBAT);
  }
  // Send command to the fingerprint scanner
  async sendCommand(command, data = new Uint8Array([])) {
    if (!this.device || !this.device.gatt.connected) {
      this.emit('connectionError', 'Bluetooth device not connected');
      return;
    }
    this.resetData();
    const size = data.length;
    const buffer = new Uint8Array(9 + size); // Adjust based on actual command structure

    // Command structure
    buffer[0] = 0x46;
    buffer[1] = 0x54;
    buffer[2] = 0x00;
    buffer[3] = 0x00;
    buffer[4] = command;
    buffer[5] = size & 0xff;
    buffer[6] = (size >> 8) & 0xff;
    buffer.set(data, 7);

    // Calculate checksum
    const checksum = calculateChecksum(buffer.subarray(0, 7 + size));
    buffer[7 + size] = checksum & 0xff;
    buffer[8 + size] = (checksum >> 8) & 0xff;

    try {
      await this.transRxCharacteristic.writeValue(buffer);
      this.commandTimeoutId = setTimeout(() => {
        console.error('Command timed out');
        this.emit('commandError', 'Command timed out');
      }, COMMAND_TIMEOUT_MS);
    } catch (error) {
      this.emit('connectionError', 'Error sending command to Bluetooth device');
    }
  }

  // Enroll a fingerprint to the host
  async enrollFingerprint() {
    await this.sendCommand(COMMANDS.CMD_ENROLHOST);
  }

  // Capture a fingerprint to the host
  async captureFingerprint() {
    await this.sendCommand(COMMANDS.CMD_CAPTUREHOST);
  }

  // Disconnect from the fingerprint scanner
  disconnect() {
    if (!this.device) return;
    console.log('Disconnecting from Bluetooth Device...');
    if (this.device.gatt.connected) {
      this.device.gatt.disconnect();
      this.emit('disconnected');
    } else {
      console.log('Bluetooth Device is already disconnected');
    }
  }
}
